import { classSet } from '@platform/ui-library';
import { FC, useMemo } from 'react';
import {
  ClassNamesConfig,
  CSSObjectWithLabel,
  default as ReactSelect,
  Props as ReactSelectProps,
  StylesConfig
} from 'react-select';
import {
  FormControlWithOptionProps,
  FormInputProps,
  Option
} from '@app/types';
import { ControlWithFormField, FormFieldControlProps } from './form-field';
import styles from './select.module.scss';

const defaultSelectClassNames: ClassNamesConfig<unknown> = {
  control: () => '',
  menu: () => classSet(['ui-select-menu', styles.menu])
};

const defaultSelectStyles: StylesConfig<unknown> = {
  control: (baseStyles, state) => {
    return {
      ...baseStyles,
      backgroundColor: state.isDisabled
        ? 'var(--theme-background-disabled)'
        : 'var(--theme-background-input)',
      borderColor: state.isFocused
        ? 'var(--theme-accent)'
        : state.isDisabled
          ? 'var(--theme-border-muted)'
          : 'var(--theme-border)',
      borderRadius: 'var(--radius-small)',
      borderWidth: '1px',
      boxShadow: state.isFocused
        ? '0 0 0 1px var(--theme-accent)'
        : 'none',
      color: 'inherit',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size)',
      minHeight: 'var(--height-input)',
      outline: 'none',
      padding: '0',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        borderColor: state.isDisabled
          ? 'var(--theme-border-muted)'
          : 'var(--theme-accent)'
      }
    };
  },
  valueContainer: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '0 var(--space)',
      cursor: 'pointer'
    };
  },
  input: (baseStyles) => {
    return {
      ...baseStyles,
      color: 'inherit',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size)',
      margin: '0',
      padding: '0'
    };
  },
  placeholder: (baseStyles, state) => {
    return {
      ...baseStyles,
      color: state.isDisabled
        ? 'var(--theme-text-disabled)'
        : 'var(--theme-text-placeholder)',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size)',
      fontStyle: 'italic',
      margin: '0'
    };
  },
  singleValue: (baseStyles, state) => {
    return {
      ...baseStyles,
      color: state?.isDisabled
        ? 'var(--theme-text-disabled)'
        : 'inherit',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size)',
      margin: '0'
    };
  },
  multiValue: (baseStyles) => {
    return {
      ...baseStyles,
      backgroundColor: 'var(--theme-background-hover)',
      borderRadius: 'var(--radius-small)',
      margin: '0 var(--space-tiny) 0 0'
    };
  },
  multiValueLabel: (baseStyles) => {
    return {
      ...baseStyles,
      color: 'inherit',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size-small)',
      padding: '0 var(--space-tiny)'
    };
  },
  multiValueRemove: (baseStyles, state) => {
    return {
      ...baseStyles,
      backgroundColor: state.isFocused
        ? 'var(--theme-error)'
        : 'transparent',
      borderRadius: '0 var(--radius-small) var(--radius-small) 0',
      color: state.isFocused
        ? 'var(--theme-background-nearest)'
        : 'var(--theme-text-secondary)',
      cursor: 'pointer',
      padding: '0 var(--space-tiny)',
      '&:hover': {
        backgroundColor: 'var(--theme-error)',
        color: 'var(--theme-background-nearest)'
      }
    };
  },
  indicatorsContainer: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '0 var(--space-tiny)'
    };
  },
  indicatorSeparator: (baseStyles, state) => {
    return {
      ...baseStyles,
      backgroundColor: state.isDisabled
        ? 'var(--theme-border-muted)'
        : 'var(--theme-border-muted)',
      margin: 'var(--space-tiny) 0'
    };
  },
  dropdownIndicator: (baseStyles, state) => {
    return {
      ...baseStyles,
      color: state.isDisabled
        ? 'var(--theme-text-disabled)'
        : 'var(--theme-text-secondary)',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      padding: '0 var(--space-tiny)',
      transition: 'color 0.2s ease, transform 0.2s ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      '&:hover': {
        color: state.isDisabled
          ? 'var(--theme-text-disabled)'
          : 'inherit'
      }
    };
  },
  clearIndicator: (baseStyles) => {
    return {
      ...baseStyles,
      color: 'var(--theme-text-secondary)',
      cursor: 'pointer',
      padding: '0 var(--space-tiny)',
      transition: 'color 0.2s ease',
      '&:hover': {
        color: 'var(--theme-error)'
      }
    };
  },
  loadingIndicator: (baseStyles) => {
    return {
      ...baseStyles,
      color: 'var(--theme-text-secondary)',
      padding: '0 var(--space-tiny)'
    };
  },
  menu: (baseStyles) => {
    return {
      ...baseStyles,
      backgroundColor: 'var(--theme-background-nearest)',
      borderRadius: 'var(--radius-small)',
      boxShadow: 'var(--shadow-min), 0 0 0 1px var(--theme-border)',
      marginTop: 'var(--space-tiny)',
      overflow: 'hidden',
      padding: '0',
      zIndex: 9999
    };
  },
  menuList: (baseStyles) => {
    return {
      ...baseStyles,
      marginBlock: 'var(--space-min)',
      maxHeight: '300px',
      padding: 'var(--space-tiny) 0'
    };
  },
  menuPortal: (baseStyles) => {
    return {
      ...baseStyles,
      zIndex: 9999
    };
  },
  option: (baseStyles, optionState) => {
    const { isFocused, isSelected, isDisabled } = optionState;
    let backgroundColor = 'transparent';
    if (isDisabled) {
      backgroundColor = 'transparent';
    } else if (isSelected && isFocused) {
      backgroundColor = 'var(--theme-background-active)';
    } else if (isSelected) {
      backgroundColor = 'var(--theme-background-active)';
    } else if (isFocused) {
      backgroundColor = 'var(--theme-background-hover)';
    }

    return {
      ...baseStyles,
      backgroundColor,
      color: isDisabled
        ? 'var(--theme-text-disabled)'
        : 'inherit',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size)',
      padding: 'var(--space-tiny) var(--space)',
      transition: 'background-color 0.15s ease',
      '&:active': {
        backgroundColor: isDisabled
          ? 'transparent'
          : 'var(--theme-background-active)'
      }
    };
  },
  group: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '0'
    };
  },
  groupHeading: (baseStyles) => {
    return {
      ...baseStyles,
      color: 'var(--theme-text-secondary)',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size-small)',
      fontWeight: '600',
      padding: 'var(--space-tiny) var(--space)',
      textTransform: 'uppercase'
    };
  },
  noOptionsMessage: (baseStyles) => {
    return {
      ...baseStyles,
      color: 'var(--theme-text-muted)',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size)',
      padding: 'var(--space)'
    };
  },
  loadingMessage: (baseStyles) => {
    return {
      ...baseStyles,
      color: 'var(--theme-text-muted)',
      fontFamily: 'inherit',
      fontSize: 'var(--font-size)',
      padding: 'var(--space)'
    };
  }
};

function mergeSelectStyleKey<TOption, K extends keyof StylesConfig>(
  key: K,
  customStyles: StylesConfig<TOption>
): StylesConfig<TOption>[K] {
  const defaultFn = defaultSelectStyles[key] as StylesConfig<TOption>[K];
  const customFn = customStyles[key];
  return ((baseStyles, state) => {
    const themed = defaultFn?.(baseStyles, state);
    return {
      ...themed,
      ...customFn?.(themed ?? baseStyles, state)
    };
  }) as StylesConfig<TOption>[K];
}

export type SelectControlProps<
  TValue,
  TOption extends Option<TValue> = Option<TValue>
> = (
  FormControlWithOptionProps<TValue | null, TOption | null>
  & FormInputProps
  & Pick<ReactSelectProps<TOption>, (
    'classNames'
    | 'isClearable'
    | 'isDisabled'
    | 'isLoading'
    | 'loadingMessage'
    | 'noOptionsMessage'
    | 'styles'
  )>
  & {
    options: TOption[] | null | undefined;
  }
);

export function SelectControl<
  TValue,
  TOption extends Option<TValue> = Option<TValue>
>({
  classNames: classNamesProp,
  onChange,
  options,
  placeholder,
  styles: stylesProp,
  value: optionValue,
  ...reactSelectProps
}: SelectControlProps<TValue, TOption>): ReturnType<FC> {
  const selectedOption = useMemo(() => {
    return options?.find(({ value }) => {
      return value === optionValue;
    });
  }, [options, optionValue]);
  const selectClassNames = useMemo(
    () => getSelectClassNames(classNamesProp),
    [classNamesProp]
  );
  const selectStyles = useMemo(
    () => getSelectStyles(stylesProp),
    [stylesProp]
  );
  return (
    <ReactSelect<TOption>
      {...reactSelectProps}
      classNames={selectClassNames}
      styles={selectStyles}
      getOptionLabel={({ label }) => {
        return label;
      }}
      tabSelectsValue={false}
      onChange={(option) => {
        onChange(option?.value ?? null, option ?? null);
      }}
      options={options ?? undefined}
      placeholder={placeholder ?? 'Search...'}
      value={selectedOption}
    />
  );
};

export type SelectProps<
  TValue,
  TOption extends Option<TValue> = Option<TValue>
> = (
  SelectControlProps<TValue, TOption> & FormFieldControlProps
);

export function Select<TValue, TOption extends Option<TValue> = Option<TValue>>(
  props: SelectProps<TValue, TOption>
): ReturnType<FC> {
  return (
    <ControlWithFormField control={SelectControl<TValue, TOption>} {...props} />
  );
}

function getSelectClassNames<TOption>(
  customClassNames?: ClassNamesConfig<TOption>
): ClassNamesConfig<TOption> {
  if (!customClassNames) {
    return defaultSelectClassNames as ClassNamesConfig<TOption>;
  }
  return {
    ...customClassNames,
    control: (state) => {
      return customClassNames.control?.(state) ?? '';
    },
    menu: (state) => {
      return classSet([
        'ui-select-menu',
        styles.menu,
        customClassNames.menu?.(state)
      ]);
    }
  };
}

function getSelectStyles<TOption>(
  customStyles?: StylesConfig<TOption>
): StylesConfig<TOption> {
  if (!customStyles) {
    return defaultSelectStyles as StylesConfig<TOption>;
  }
  const mergedStyles = Object.fromEntries(
    (Object.keys(defaultSelectStyles) as (keyof StylesConfig)[]).map((key) => {
      const styleKey = key as keyof StylesConfig;
      return [styleKey, mergeSelectStyleKey(styleKey, customStyles)];
    })
  ) as Partial<StylesConfig<TOption>>;
  const extraCustomStyles = Object.fromEntries(
    (Object.keys(customStyles) as (keyof StylesConfig)[]).filter((key) => !(key in defaultSelectStyles)).map((key) => {
      return [key, customStyles[key]];
    })
  ) as Partial<StylesConfig<TOption>>;
  return { ...mergedStyles, ...extraCustomStyles };
}

SelectControl.styleAddDot = (color = 'transparent'): Partial<CSSObjectWithLabel> => {
  return {
    alignItems: 'center',
    display: 'flex',
    overflow: 'visible',
    ':after': {
      backgroundColor: color,
      borderRadius: 'var(--radius-small)',
      content: '" "',
      display: 'block',
      flexShrink: 0,
      height: '8px',
      marginLeft: 5,
      width: '8px'
    }
  };
};
